import { FC, useContext, useEffect, useMemo } from "react";
import { alpha, darken, lighten, Link, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useTheme } from "@mui/material";
import DefaultLayoutContext from "@/contexts/layout/DefaultLayoutContext";
import AppSettings from "@/AppSettings";
import { ChosenThemeActionContext } from "@/contexts/chosenThemeActionContext";
import { ChosenThemeContext } from "@/contexts/styles/chosenThemeContext";
import { useBreadcrumbs } from "@/contexts/layout/BreadcrumbContext";
import { faDna, faFileChartColumn } from '@fortawesome/pro-light-svg-icons';

interface IReportItem {
    label: string,
    url: string
}

const Reports: FC = () => {
        
    const { setBreadcrumbs } = useBreadcrumbs();
    
    useEffect(() => {
        setBreadcrumbs([
            { label: "Trial", to: "/", icon: faDna },
            { label: "Reports", to: "/reports", icon: faFileChartColumn },
        ]);
    }, []);

    const chosenTheme = useContext(ChosenThemeContext);
    const setChosenTheme = useContext(ChosenThemeActionContext);

    const theme = useTheme();
    const reports = useMemo<IReportItem[]>(() => [
        { // @TODO: Insert URLs once the reports have been put on the server
            label: "Overall Trial Redaction Status",
            url: `${AppSettings.reportUrl}/${AppSettings.reportDir}/OverallTrialRedactionStatus`
        },
        {
            label: "Overall Trial Graph",
            url: `${AppSettings.reportUrl}/${AppSettings.reportDir}/OverallTrialRedactionGraph`
        },
        {
            label: "Trial and Site Report",
            url: `${AppSettings.reportUrl}/${AppSettings.reportDir}/TrialAndSiteRedactionStatus`
        },
        {
            label: "Trial and Site Graph",
            url: `${AppSettings.reportUrl}/${AppSettings.reportDir}/TrialAndSiteRedactionGraph`
        },
        {
            label: "LSG and Site Report",
            url: `${AppSettings.reportUrl}/${AppSettings.reportDir}/LsgAndSiteRedactionStatus?Token=EmailLink`
        },
        {
            label: "LSG Graph",
            url: `${AppSettings.reportUrl}/${AppSettings.reportDir}/LsgRedactionGraph`
        },
        {
            label: "Reasons for Redacting",
            url: `${AppSettings.reportUrl}/${AppSettings.reportDir}/OverallRedactionReasons`
        },
        {
            label: "Reasons for Redacting (Site/LSG Level)",
            url: `${AppSettings.reportUrl}/${AppSettings.reportDir}/LsgAndSiteRedactionReasons`
        }
    ], [AppSettings.reportDir, AppSettings.reportUrl]);

    const { setTrialLinkEnabled } = useContext(DefaultLayoutContext);

    useEffect(() => {
        if(setTrialLinkEnabled) {
            setTrialLinkEnabled(true);
        }
    }, [])

    return (
        <>
            <Paper>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    Report
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {
                                reports.map((report, index) => (
                                    <TableRow
                                        key={index}
                                    >
                                            <TableCell
                                                sx={{
                                                    padding: 0,
                                                }}
                                            >
                                                <Link
                                                    href={report.url}
                                                    underline="none"
                                                    sx={{
                                                        display: "flex",
                                                        flexGrow: 1,
                                                        color: chosenTheme === 'light' ? theme.palette.primary.main : lighten(theme.palette.primary.main, 0.4),
                                                        padding: 2,
                                                        "&:hover": {
                                                            backgroundColor:  chosenTheme === 'light' ? lighten(alpha(theme.palette.primary.main, 0.15), 0.4) : darken(alpha(theme.palette.primary.main, 0.15), 0.1),
                                                        }
                                                    }}
                                                >
                                                    {report.label}
                                                </Link>
                                            </TableCell>
                                    </TableRow>
                                ))
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </>
    );
}

export default Reports;